import { useParams } from "react-router-dom";

import {
  BaseType,
  QuestionFieldFragment,
  QuestionType,
} from "@/generated/graphql";
import { useAssessmentSettingsFragment } from "@/graphql/assessment-selectors";

import * as styles from "./answer-form.css";
import { BlanksResponseForm } from "./blanks-response-form";
import { BooleanResponseForm } from "./boolean-response-form";
import { ExtendedResponseForm } from "./extended-response-form";
import { HotspotResponseForm } from "./hotspot-response-form/hotspot-response-form";
import { MatchingResponseForm } from "./matching-response-form";
import { MultiResponseForm } from "./multi-response-form";
import { NumberResponseForm } from "./number-response-form";
import { RangeResponseForm } from "./range-response-form";
import { ShortResponseForm } from "./short-response-form";
import { VideoResponseForm } from "./video-response-form/video-response-form";

interface Props {
  answerBlockId: string;
  referencesAnswerBlockId: string | null;
  questionType: QuestionType;
  fields: QuestionFieldFragment[];
  /** Make the answer response form readonly */
  isReadonly?: boolean;
  /** Whether options should be shuffle */
  shuffle?: boolean;
}

export function ResponseForm(props: Props) {
  const fields = props.fields ?? [];
  const singleField = fields[0];

  const { assessmentId = "" } = useParams<{ assessmentId: string }>();
  const settings = useAssessmentSettingsFragment(assessmentId);

  if (!singleField) return null;

  switch (props.questionType) {
    case QuestionType.Extended: {
      const wordLimit =
        singleField.interaction.__typename === "ExtendedInteraction"
          ? singleField.interaction.wordLimit
          : undefined;
      const attachmentEnabled =
        singleField.interaction.__typename === "ExtendedInteraction"
          ? singleField.interaction.attachmentEnabled
          : undefined;
      return (
        <>
          <ExtendedResponseForm
            answerBlockId={props.answerBlockId}
            referencesAnswerBlockId={props.referencesAnswerBlockId}
            isReadonly={props.isReadonly}
            wordLimit={wordLimit ?? undefined}
            attachmentEnabled={!!attachmentEnabled}
            isLdbExamMode={settings?.examLdbEnabled ?? false}
          />
        </>
      );
    }
    case QuestionType.Truefalse:
      return (
        <div className={styles.response}>
          <BooleanResponseForm
            identifier={singleField.identifier}
            answerBlockId={props.answerBlockId}
            isReadonly={props.isReadonly}
          />
        </div>
      );

    case QuestionType.Matching:
      if (singleField.interaction.__typename === "MatchInteraction") {
        return (
          <MatchingResponseForm
            fieldIdentifier={singleField.identifier}
            targetSet={singleField.interaction.targetSet}
            sourceSet={singleField.interaction.sourceSet}
            shuffle={props.shuffle}
            answerBlockId={props.answerBlockId}
            isReadonly={props.isReadonly}
          />
        );
      }
      return null;

    case QuestionType.Mcq:
      if (singleField.interaction.__typename === "ChoiceInteraction") {
        return (
          <div className={styles.response}>
            <MultiResponseForm
              identifier={singleField.identifier}
              answerBlockId={props.answerBlockId}
              isReadonly={props.isReadonly}
              shuffle={props.shuffle}
              maxChoices={singleField.interaction.maxChoices}
              choices={singleField.interaction.choices}
            />
          </div>
        );
      }
      return null;

    case QuestionType.Numerical:
      if (singleField.interaction.__typename === "TextEntryInteraction") {
        const attachmentEnabled = singleField.interaction.attachmentEnabled;
        if (singleField.interaction.baseType === BaseType.Float) {
          return (
            <NumberResponseForm
              identifier={singleField.identifier}
              answerBlockId={props.answerBlockId}
              isReadonly={props.isReadonly}
              attachmentEnabled={!!attachmentEnabled}
              isLdbExamMode={settings?.examLdbEnabled ?? false}
            />
          );
        }

        // For range type numerical question
        if (singleField.interaction.baseType === BaseType.Range) {
          return (
            <RangeResponseForm
              identifier={singleField.identifier}
              answerBlockId={props.answerBlockId}
              isReadonly={props.isReadonly}
              attachmentEnabled={!!attachmentEnabled}
              isLdbExamMode={settings?.examLdbEnabled ?? false}
            />
          );
        }
      }
      return null;

    case QuestionType.Short: {
      const attachmentEnabled =
        singleField.interaction.__typename === "TextEntryInteraction"
          ? singleField.interaction.attachmentEnabled
          : undefined;
      return (
        <>
          <ShortResponseForm
            identifier={singleField.identifier}
            answerBlockId={props.answerBlockId}
            isReadonly={props.isReadonly}
            isLdbExamMode={settings?.examLdbEnabled ?? false}
            attachmentEnabled={!!attachmentEnabled}
          />
        </>
      );
    }

    case QuestionType.Blanks:
      return (
        <BlanksResponseForm
          questionFields={props.fields}
          answerBlockId={props.answerBlockId}
          isReadonly={props.isReadonly}
        />
      );

    case QuestionType.Hotspot:
      return (
        <HotspotResponseForm
          questionFields={props.fields}
          answerBlockId={props.answerBlockId}
          isReadonly={props.isReadonly}
          isPositionObjectHotspot={
            singleField.interaction.__typename === "PositionObjectInteraction"
          }
        />
      );

    case QuestionType.Video:
      if (
        settings &&
        singleField.interaction.__typename === "SocraticInteraction"
      ) {
        return (
          <VideoResponseForm
            answerBlockId={props.answerBlockId}
            isReadonly={props.isReadonly}
            maxAttempts={singleField.interaction.maxAttempts}
            responseTimeSeconds={singleField.interaction.responseTime}
            preparationTimeSeconds={singleField.interaction.readingTime}
            vivaAssessmentSettings={settings}
          />
        );
      }
  }

  return null;
}
